import React, { useMemo, useState } from 'react';
import { formatCurrency } from 'utils';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { MUITable } from 'components/shared/MUITable';
import { ProposalSaleType } from '@trnsact/trnsact-shared-types';
import { getPersonaName } from 'modules/proposals/lib/getPersonaName';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { proposalSaleTypeToViewText } from 'modules/proposals/constants';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { MUITableColumn, MUIBaseEntity } from 'components/shared/MUITable/types';
import { ActionCell, DateCell, SimpleTextCell, StatusCell } from 'modules/proposals/ui/tables/cells';

import { deskingActions } from '../../model';
import { GET_PROPOSALS_TABLE_DATA } from '../../api';
import { ProposalsTableDataResponse, ProposalsTableDataVariables } from '../../api/types';

interface Props {
  voId: string;
  vendorGUID: string;
}

interface ProposalRow extends MUIBaseEntity {
  id: string;
  owner: string;
  status: string;
  contact: string;
  actions: string;
  businessName: string;
  viewSaleType: string;
  createdDateTime: string;
  amountRequested: string;
  saleType: ProposalSaleType;
}

export const ProposalsTable = ({ vendorGUID, voId }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [pagination, setPagination] = useState({ page: 1, perPage: 5 });

  const { data, loading } = useQuery<ProposalsTableDataResponse, ProposalsTableDataVariables>(
    GET_PROPOSALS_TABLE_DATA,
    {
      skip: !voId,
      variables: {
        voId,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const proposals = data?.proposals ?? [];

  const getProposalLink = (proposal: ProposalRow) => {
    const url = generateProposalPageURL(proposal.id, vendorGUID);
    window.open(url, '_blank');
  };

  const handleGoToProposal = (proposal: ProposalRow) => {
    window.open(`/proposalDetail/${proposal.id}?from=/vo/${voId}`, '_blank');
  };

  const handleEditProposal = (proposal: ProposalRow) => {
    const { id, saleType } = proposal;

    history.push({
      hash: '#desking',
      pathname: `/vo/${voId}`,
      state: {
        proposalId: id,
        proposalSaleType: saleType,
      },
    });

    dispatch(deskingActions.resetStructureAndMenuOptions());
    dispatch(deskingActions.setProposalDetailsForEdit({ id: proposal.id!, saleType: proposal.saleType! }));
  };

  const tableData: ProposalRow[] = proposals.map(proposal => ({
    id: proposal.id!,
    actions: '',
    status: proposal.status ?? '',
    contact: getPersonaName(proposal),
    owner: proposal.salesPersonName ?? '',
    businessName: proposal.businessName ?? '',
    createdDateTime: proposal.createdDateTime ?? '',
    saleType: proposal.saleType ?? ProposalSaleType.FinanceOnly,
    viewSaleType: proposalSaleTypeToViewText[proposal.saleType!] ?? '',
    amountRequested: proposal.amountRequested != null ? String(proposal.amountRequested) : '',
  }));

  const paginatedData = useMemo(() => {
    const start = (pagination.page - 1) * pagination.perPage;
    return tableData.slice(start, start + pagination.perPage);
  }, [tableData, pagination]);

  const columns: MUITableColumn<ProposalRow>[] = [
    {
      header: 'Created On',
      renderKey: 'createdDateTime',
      renderCell: row => <DateCell proposal={row} />,
    },
    {
      header: 'Company',
      renderKey: 'businessName',
      renderCell: row => <SimpleTextCell value={row.businessName} />,
    },
    {
      header: 'Contact',
      renderKey: 'contact',
      renderCell: row => <SimpleTextCell value={row.contact} />,
    },
    {
      header: 'Sale Type',
      renderKey: 'saleType',
      renderCell: row => <SimpleTextCell value={row.viewSaleType} />,
    },
    {
      header: 'Amount',
      renderKey: 'amountRequested',
      renderCell: row => (
        <SimpleTextCell formatter={formatCurrency} value={row.amountRequested ? Number(row.amountRequested) : ''} />
      ),
    },
    {
      header: 'Proposal Status',
      renderKey: 'status',
      renderCell: row => <StatusCell proposal={row} />,
    },
    {
      header: 'Owner',
      renderKey: 'owner',
      renderCell: row => <SimpleTextCell value={row.owner} />,
    },
    {
      header: 'Actions',
      renderKey: 'actions',
      renderCell: row => (
        <ActionCell
          proposal={row}
          onEditProposal={handleEditProposal}
          onGoToProposal={handleGoToProposal}
          onGoToProposalDetail={getProposalLink}
        />
      ),
    },
  ];

  return (
    <CardContainer title="Proposals">
      <MUITable<ProposalRow>
        columns={columns}
        isLoading={loading}
        tableData={paginatedData}
        pagination={{
          page: pagination.page,
          total: tableData.length,
          perPage: pagination.perPage,
          onChange: next => setPagination(prev => ({ ...prev, ...next })),
        }}
      />
    </CardContainer>
  );
};
